import { FC } from 'react'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { useLanguage } from 'services/i18n/i18n.hooks'
import theme from 'styles/theme'

const METER_META: Record<
  string,
  { i18n: string; defaultMessage: string; unit: string }
> = {
  electricity: {
    i18n: 'acceptanceProtocolMediaMeter.wattHourMeter',
    defaultMessage: 'Energia elektryczna',
    unit: 'kWh'
  },
  watthourmeter: {
    i18n: 'acceptanceProtocolMediaMeter.wattHourMeter',
    defaultMessage: 'Energia elektryczna',
    unit: 'kWh'
  },
  hotwater: {
    i18n: 'acceptanceProtocolMediaMeter.hotWaterMeter',
    defaultMessage: 'Woda ciepła',
    unit: 'm³'
  },
  hotwatermeter: {
    i18n: 'acceptanceProtocolMediaMeter.hotWaterMeter',
    defaultMessage: 'Woda ciepła',
    unit: 'm³'
  },
  coldwater: {
    i18n: 'acceptanceProtocolMediaMeter.coldWaterMeter',
    defaultMessage: 'Woda zimna',
    unit: 'm³'
  },
  coldwatermeter: {
    i18n: 'acceptanceProtocolMediaMeter.coldWaterMeter',
    defaultMessage: 'Woda zimna',
    unit: 'm³'
  },
  heating: {
    i18n: 'acceptanceProtocolMediaMeter.heatingMeter',
    defaultMessage: 'Ogrzewanie',
    unit: 'GJ'
  },
  heatingmeter: {
    i18n: 'acceptanceProtocolMediaMeter.heatingMeter',
    defaultMessage: 'Ogrzewanie',
    unit: 'GJ'
  },
  airconditioning: {
    i18n: 'acceptanceProtocolMediaMeter.airConditioningMeter',
    defaultMessage: 'Klimatyzacja',
    unit: 'kWh'
  },
  airconditioningmeter: {
    i18n: 'acceptanceProtocolMediaMeter.airConditioningMeter',
    defaultMessage: 'Klimatyzacja',
    unit: 'kWh'
  }
}

const normalizeMeterType = (meterType: string) => meterType.toLowerCase().replace(/[\s_-]/g, '')

const formatMeterValue = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'n/a'
  }

  const hasFraction = !Number.isInteger(value)
  const [integer, fraction] = (hasFraction ? value.toFixed(1) : String(value)).split('.')
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  return hasFraction ? `${grouped},${fraction}` : grouped
}

const ProtocolMetersReadings: FC<{
  readings: {
    id: number
    meterType: string
    meterPhoto: string
    meterValue: number
    readingDate: string
  }[]
}> = ({ readings }) => {
  const { formatMessage } = useIntl()
  const { dateAdapter } = useLanguage()

  const visibleReadings = readings.filter(({ meterType }) => meterType !== 'Gas')

  if (!visibleReadings.length) {
    return (
      <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
        {formatMessage({ id: 'common.noData', defaultMessage: 'Brak danych' })}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))'
        },
        gap: '14px'
      }}>
      {visibleReadings.map((reading) => {
        const meta = METER_META[normalizeMeterType(reading.meterType)]
        const label = meta
          ? formatMessage({ id: meta.i18n, defaultMessage: meta.defaultMessage })
          : reading.meterType
        const unit = meta?.unit
        const dateLabel = reading.readingDate
          ? dayjs(reading.readingDate).locale(dateAdapter).format('DD.MM.YYYY')
          : 'n/a'

        return (
          <Box
            key={reading.id}
            sx={{
              backgroundColor: '#f9fafb',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                color: theme.r4r.grey
              }}>
              {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <Typography sx={{ fontSize: '22px', fontWeight: 800, color: theme.r4r.navy, lineHeight: 1.2 }}>
                {formatMeterValue(reading.meterValue)}
              </Typography>
              {!!unit && (
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.r4r.grey }}>
                  {unit}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '10px', color: theme.r4r.grey, pt: '2px' }}>
              {formatMessage(
                {
                  id: 'additionalServicesProtocol.reading',
                  defaultMessage: 'Odczyt: {date}'
                },
                { date: dateLabel }
              )}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export default ProtocolMetersReadings
